import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, BookOpen, GraduationCap, Building2, Plus } from 'lucide-react';
import { PageHeader, Button, SearchBar, Card, CardHeader, CardTitle, CardDescription, CardContent, EmptyState } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import { resourceCategoryUiToApi } from '@/lib/resourceCategoryMap';
import type { ResourceCategory } from '@/data/resources';
import { deleteResourcePost, getResourcePostList } from '@/services/resourcePostService';

const CATEGORY_OPTIONS: { value: ResourceCategory; labelKey: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { value: 'all', labelKey: 'categoryAll', icon: FileText },
  { value: 'productInfo', labelKey: 'categoryProductInfo', icon: FileText },
  { value: 'guide', labelKey: 'categoryGuide', icon: BookOpen },
  { value: 'education', labelKey: 'categoryEducation', icon: GraduationCap },
  { value: 'simulationRoom', labelKey: 'categorySimulationRoom', icon: Building2 },
];

/**
 * 리소스 목록 페이지 — GET /resource-post/list + language
 */
export default function ResourceList(): React.ReactElement {
  const { t } = useTranslation('menu');
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ResourceCategory>('all');
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const lang = useApiLanguage();
  const queryClient = useQueryClient();

  const listParams = useMemo(() => {
    const base: Parameters<typeof getResourcePostList>[0] = {
      page,
      pageSize,
      language: lang,
      ...(searchQuery.trim() ? { searchText: searchQuery.trim() } : {}),
    };
    if (categoryFilter !== 'all') {
      base.category = resourceCategoryUiToApi(categoryFilter);
    }
    return base;
  }, [page, pageSize, lang, categoryFilter, searchQuery]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['resource-post', 'list', listParams, isAdmin],
    queryFn: () => getResourcePostList(listParams, { skipAuth: !isAdmin }),
  });

  const deleteMut = useMutation({
    mutationFn: (resourcePostId: number) => deleteResourcePost(resourcePostId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resource-post'] });
    },
  });

  const handleDeleteResource = (resourcePostId: number) => {
    if (window.confirm('이 자료를 삭제하시겠습니까?')) {
      deleteMut.mutate(resourcePostId);
    }
  };

  const items = data?.resourcePosts ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const wrapperClass = 'max-w-[1280px] mx-auto px-4 py-8';
  return (
    <div className={wrapperClass}>
      <PageHeader
        title={t('resources')}
        description={t('resourcePage.description')}
        rightContent={
          isAdmin ? (
            <Link to={ROUTES.ADMIN.RESOURCES_CREATE}>
              <Button variant="lightdark" size="md">
                <Plus size={16} className="mr-2" />
                {t('resourcePage.createButton')}
              </Button>
            </Link>
          ) : null
        }
      />

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {CATEGORY_OPTIONS.map(({ value, labelKey, icon: Icon }) => (
            <Button
              key={value}
              variant={categoryFilter === value ? 'lightdark' : 'outline'}
              size="md"
              onClick={() => {
                setCategoryFilter(value);
                setPage(1);
              }}
            >
              <Icon size={16} className="mr-1.5 shrink-0" />
              {t(`resourcePage.${labelKey}`)}
            </Button>
          ))}
        </div>
        <div className="w-full sm:w-72 shrink-0">
          <SearchBar
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('resourcePage.searchPlaceholder')}
          />
        </div>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="text-gray-500">불러오는 중…</p>
        ) : isError ? (
          <p className="text-red-600">{error instanceof Error ? error.message : '오류가 발생했습니다.'}</p>
        ) : items.length === 0 ? (
          <EmptyState
            title={t('resourcePage.emptyTitle')}
            description={t('resourcePage.emptyDescription')}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((row) => {
                const idStr = String(row.id);
                return (
                  <Card key={row.id} className="flex flex-col h-full hover:shadow-md transition-shadow">
                    <Link
                      to={isAdmin ? ROUTES.ADMIN.RESOURCE_DETAIL(idStr) : ROUTES.COMMON.RESOURCE_DETAIL(idStr)}
                      className="flex flex-col flex-1 no-underline"
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-gray-900">{row.title}</CardTitle>
                        <CardDescription className="line-clamp-2 text-gray-600">{row.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0 mt-auto">
                        <span className="text-sm font-medium text-brand-600 hover:text-brand-700">
                          {t('resourcePage.readMore')} →
                        </span>
                      </CardContent>
                    </Link>
                    {isAdmin && (
                      <CardContent className="pt-0 pb-4 flex justify-end gap-2">
                        <Link to={ROUTES.ADMIN.RESOURCES_EDIT(idStr)}>
                          <Button variant="outline" size="sm">
                            {t('resourcePage.editButton')}
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteResource(row.id)}
                          disabled={deleteMut.isPending}
                          className="border-red-600 text-red-600 hover:border-red-700 hover:bg-red-50 hover:text-red-700"
                        >
                          삭제
                        </Button>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
            {totalPages > 1 ? (
              <div className="mt-8 flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  이전
                </Button>
                <span className="self-center text-sm text-gray-600">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  다음
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
